import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PAGES, canAccess } from "@/lib/permissions";
import BellButton from "@/components/BellButton";
import {
  LayoutDashboard,
  Warehouse,
  PackagePlus,
  ChefHat,
  Factory,
  Truck,
  Inbox,
  History,
  Settings,
  LogOut,
  Coffee,
  Trash2,
  ArrowLeftRight,
  Menu,
  X,
  Clock,
  Users,
  Download,
  ClipboardList,
} from "lucide-react";
import { useInstall } from "@/contexts/InstallContext";

const ICONS = {
  dashboard: LayoutDashboard,
  attendance: Clock,
  warehouses: Warehouse,
  receipts: PackagePlus,
  recipes: ChefHat,
  production: Factory,
  dispatch: Truck,
  branch_receipts: Inbox,
  transfers: ArrowLeftRight,
  writeoffs: Trash2,
  inventory: ClipboardList,
  attendance_admin: Users,
  history: History,
};

const ROLE_LABEL = {
  admin: "Administrátor",
  manager: "Manažer",
  obsluha: "Obsluha",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const install = useInstall();

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const visiblePages = PAGES.filter((p) => canAccess(user, p.key));

  const SidebarContent = (
    <>
      <div className="px-6 pt-7 pb-6 border-b border-[#E5E1D8] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#3A2D27] text-[#D4A373] grid place-items-center">
            <Coffee className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
              Caféčko
            </div>
            <div className="label-eyebrow">Výrobní ERP</div>
          </div>
        </div>
        <button
          data-testid="mobile-menu-close"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden p-2 -mr-2 rounded-md text-[#57534E] hover:bg-[#F9F6F0]"
          aria-label="Zavřít menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {visiblePages.map((item) => {
          const Icon = ICONS[item.key] || Coffee;
          return (
            <NavLink
              key={item.key}
              to={item.path}
              end={item.end}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 px-3 py-3 lg:py-2.5 rounded-lg text-base lg:text-sm transition-colors",
                  isActive
                    ? "bg-[#F9F6F0] text-[#3A2D27] font-medium border border-[#E5E1D8]"
                    : "text-[#57534E] hover:bg-[#F9F6F0]",
                ].join(" ")
              }
            >
              <Icon className="w-5 h-5 lg:w-4 lg:h-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          );
        })}

        {user?.role === "admin" && (
          <NavLink
            to="/administrace"
            data-testid="nav-admin"
            className={({ isActive }) =>
              [
                "flex items-center gap-3 px-3 py-3 lg:py-2.5 rounded-lg text-base lg:text-sm transition-colors mt-4 border-t pt-4 border-[#E5E1D8]",
                isActive
                  ? "bg-[#F9F6F0] text-[#3A2D27] font-medium border border-[#E5E1D8]"
                  : "text-[#57534E] hover:bg-[#F9F6F0]",
              ].join(" ")
            }
          >
            <Settings className="w-5 h-5 lg:w-4 lg:h-4 shrink-0" />
            Administrace
          </NavLink>
        )}
      </nav>

      <div className="border-t border-[#E5E1D8] p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#D4A373] text-[#1C1917] grid place-items-center font-medium">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-[#57534E] truncate">
              {ROLE_LABEL[user?.role] || "Obsluha"}
            </div>
          </div>
        </div>
        {!install.isStandalone && (
          <button
            data-testid="install-app-button"
            onClick={() => install.promptInstall()}
            className="w-full flex items-center justify-center gap-2 text-sm py-2.5 rounded-lg border border-[#D4A373] text-[#3A2D27] bg-[#D4A37326] hover:bg-[#D4A37340] transition-colors mb-2"
          >
            <Download className="w-4 h-4" />
            Přidat na plochu
          </button>
        )}
        <div className="flex items-center justify-center mb-2">
          <BellButton />
        </div>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 text-sm py-2.5 rounded-lg border border-[#E5E1D8] hover:bg-[#F9F6F0] transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Odhlásit se
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#F9F6F0] text-[#1C1917]">
      {/* Desktop sidebar */}
      <aside
        data-testid="app-sidebar"
        className="hidden lg:flex w-72 shrink-0 border-r border-[#E5E1D8] bg-[#FFFFFF] flex-col sticky top-0 h-screen self-start"
      >
        {SidebarContent}
      </aside>

      {/* Mobile drawer + backdrop */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu-backdrop"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
        />
      )}
      <aside
        data-testid="mobile-sidebar"
        className={`lg:hidden fixed top-0 left-0 bottom-0 w-72 max-w-[85vw] bg-white border-r border-[#E5E1D8] flex flex-col z-50 transform transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        {SidebarContent}
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header
          data-testid="mobile-topbar"
          className="lg:hidden sticky top-0 z-30 bg-white border-b border-[#E5E1D8] flex items-center justify-between px-4 py-3"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}
        >
          <button
            data-testid="mobile-menu-open"
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 rounded-md text-[#3A2D27] hover:bg-[#F9F6F0]"
            aria-label="Otevřít menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#3A2D27] text-[#D4A373] grid place-items-center">
              <Coffee className="w-4 h-4" />
            </div>
            <div className="text-base font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
              Caféčko
            </div>
          </div>
          <div className="flex items-center gap-1">
            <BellButton />
            <div className="w-9 h-9 rounded-full bg-[#D4A373] text-[#1C1917] grid place-items-center font-medium text-sm">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
